/**
 * SOC Analyst landing — /app/landing/soc
 *
 * Live operations: open correlations, ransomware proximity, IOCs, AI
 * safety incidents, auto-tickets. Auto-refresh every 30s on the live
 * correlation panel — SOC reads are time-sensitive.
 */
import { useEffect, useState, useCallback } from 'react'
import {
  Skull, Bot, Eye, RefreshCw, Network,
} from 'lucide-react'
import { api } from '../lib/api'
import {
  LandingHeader, HeroTile, TwoColumnBody, Panel, EmptyState,
  StickyFooter, ListRow, safeNum,
} from '../components/landing/LandingShell'

interface Correlation { id: string; severity: string; title?: string; rule_name?: string; created_at?: string }
interface Asset { golden_id: string; total?: number; band?: string; archetype?: string }
interface AiIncident { id: string; title?: string; severity?: string; created_at?: string }
interface RansomwareVictim { victim: string; sector?: string; date?: string }

export default function SocLandingPage() {
  const [refreshing, setRefreshing]     = useState(false)
  const [correlations, setCorrelations] = useState<Correlation[]>([])
  const [kev, setKev]                   = useState<number>(0)
  const [iocs, setIocs]                 = useState<number>(0)
  const [ransomware, setRansomware]     = useState<RansomwareVictim[]>([])
  const [aiIncidents, setAiIncidents]   = useState<AiIncident[]>([])
  const [tickets, setTickets]           = useState<number>(0)
  const [exposed, setExposed]           = useState<Asset[]>([])
  const [actorActivity, setActorActivity] = useState<{ actor: string; count: number }[]>([])

  const loadAll = useCallback(() => {
    setRefreshing(true)
    const j = (p: Promise<any>) => p.catch(() => ({ data: null }))
    Promise.all([
      j(api.get('/api/cdm/correlations', { params: { state: 'open', limit: 8 } })),
      j(api.get('/api/cyber/cves', { params: { severity: 'critical', limit: 1 } })),
      j(api.get('/api/cyber/feed', { params: { signal_type: 'ioc', limit: 1 } })),
      j(api.get('/api/cyber/feed', { params: { signal_type: 'ransomware', limit: 5 } })),
      j(api.get('/api/ai-incidents', { params: { limit: 5, hours: 24 } })),
      j(api.get('/api/cdm/auto-ticket/tickets', { params: { state: 'open' } })),
      j(api.get('/api/cdm/risk', { params: { limit: 5 } })),
      j(api.get('/api/cdm/threat-attribution', { params: { limit: 5 } })),
    ]).then(([c, k, ioc, r, ai, tk, ex, act]) => {
      setCorrelations(c.data?.correlations || c.data?.results || [])
      setKev(k.data?.total ?? k.data?.count ?? (k.data?.cves?.length ?? 0))
      setIocs(ioc.data?.total ?? ioc.data?.count ?? (ioc.data?.items?.length ?? 0))
      setRansomware(r.data?.items || r.data?.results || [])
      setAiIncidents(ai.data?.incidents || ai.data?.results || [])
      setTickets(tk.data?.tickets?.length ?? tk.data?.count ?? 0)
      setExposed(ex.data?.assets || ex.data?.results || [])
      const acts = (act.data?.actors || act.data?.results || []) as any[]
      setActorActivity(acts.slice(0, 7).map(a => ({ actor: a.actor, count: a.ttp_count ?? a.exposure ?? 0 })))
    }).finally(() => setRefreshing(false))
  }, [])

  // SOC has two cadences: 30s for correlations, 60s for everything
  // else. We do the simple thing here (60s) since loadAll is one batch.
  // The 30s correlation refresh runs on a smaller fetch.
  useEffect(() => {
    loadAll()
    const slow = setInterval(loadAll, 60_000)
    const fast = setInterval(() => {
      api.get('/api/cdm/correlations', { params: { state: 'open', limit: 8 } })
        .then(({ data }) => setCorrelations(data?.correlations || data?.results || []))
        .catch(() => {})
    }, 30_000)
    return () => { clearInterval(slow); clearInterval(fast) }
  }, [loadAll])

  const max = Math.max(1, ...actorActivity.map(a => a.count))

  return (
    <div className="space-y-4 animate-fade-in pb-16">
      <LandingHeader
        title="SOC Landing"
        subtitle="live · correlations · IOCs · ransomware · AI safety"
        persona="soc"
        onRefresh={loadAll}
        refreshing={refreshing}
      />

      {/* Hero — 6 tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <HeroTile label="Open correlations" value={safeNum(correlations.length)}
          tone={correlations.length > 0 ? 'rose' : 'emerald'}
          sub="auto-refresh 30s" to="/app/cdm/correlations" />
        <HeroTile label="Critical KEV" value={safeNum(kev)}
          tone={kev > 0 ? 'rose' : 'emerald'} to="/app/cyber" />
        <HeroTile label="Active IOCs" value={safeNum(iocs)}
          tone={iocs > 0 ? 'amber' : 'emerald'} to="/app/cyber-warroom" />
        <HeroTile label="Ransomware (24h)" value={safeNum(ransomware.length)}
          tone={ransomware.length > 0 ? 'amber' : 'emerald'} to="/app/ransomware" />
        <HeroTile label="AI safety (24h)" value={safeNum(aiIncidents.length)}
          tone={aiIncidents.length > 0 ? 'amber' : 'emerald'} to="/app/ai-incidents" />
        <HeroTile label="Auto-tickets" value={safeNum(tickets)}
          tone={tickets > 0 ? 'amber' : 'emerald'} to="/app/cdm/remediation" />
      </div>

      <TwoColumnBody
        left={(
          <>
            <Panel title="Live correlations · open" viewAllTo="/app/cdm/correlations">
              {correlations.length === 0 ? (
                <EmptyState message="No open correlations."
                  configureTo="/app/cdm/correlation-rules" configureLabel="manage rules" />
              ) : (
                <div className="space-y-1">
                  {correlations.slice(0, 5).map(c => (
                    <ListRow key={c.id}
                      to="/app/cdm/correlations"
                      primary={c.title || c.rule_name || c.id}
                      secondary={c.created_at}
                      right={<span className={
                        c.severity === 'critical' ? 'text-rose-light'
                          : c.severity === 'high' ? 'text-amber-light' : 'text-text-secondary'
                      }>{c.severity}</span>} />
                  ))}
                </div>
              )}
            </Panel>

            <Panel title="Recent ransomware victims" viewAllTo="/app/ransomware">
              {ransomware.length === 0 ? (
                <EmptyState message="No recent ransomware victims observed." />
              ) : (
                <div className="space-y-1">
                  {ransomware.slice(0, 5).map((r, i) => (
                    <ListRow key={i}
                      primary={r.victim}
                      secondary={r.sector}
                      right={r.date} />
                  ))}
                </div>
              )}
            </Panel>
          </>
        )}
        right={(
          <>
            <Panel title="Threat-actor activity" viewAllTo="/app/threat-actors">
              {actorActivity.length === 0 ? (
                <EmptyState message="Threat-attribution lens has no data yet."
                  configureTo="/app/cdm/attribution" />
              ) : (
                <div className="space-y-1.5">
                  {actorActivity.map(a => (
                    <div key={a.actor} className="text-[11px]">
                      <div className="flex justify-between">
                        <span className="text-text-primary truncate">{a.actor}</span>
                        <span className="font-mono text-text-secondary">{a.count}</span>
                      </div>
                      <div className="h-1 bg-surface-raised rounded-full overflow-hidden">
                        <div className="h-full bg-amber/60" style={{ width: `${(a.count / max) * 100}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Panel>

            <Panel title="Top exposed assets" viewAllTo="/app/cdm">
              {exposed.length === 0 ? (
                <EmptyState message="No risk-scored assets yet."
                  configureTo="/app/cdm/adapters" />
              ) : (
                <div className="space-y-1">
                  {exposed.slice(0, 5).map(a => (
                    <ListRow key={a.golden_id}
                      to={`/app/cdm/investigation/${a.golden_id}`}
                      primary={<span className="font-mono text-[10px]">{a.golden_id.slice(0, 12)}…</span>}
                      secondary={a.archetype}
                      right={a.band ? <span className={a.band === 'critical' ? 'text-rose-light' : 'text-amber-light'}>{a.total} · {a.band}</span> : ''} />
                  ))}
                </div>
              )}
            </Panel>
          </>
        )}
      />

      <StickyFooter actions={[
        { label: 'Cyber Situation Room', icon: Skull,    tone: 'primary',   to: '/app/cyber-warroom' },
        { label: 'Acknowledge corr.',    icon: Eye,      tone: 'secondary', to: '/app/cdm/correlations' },
        { label: 'Run drift scan',       icon: RefreshCw, tone: 'ghost',     to: '/app/drift' },
        { label: 'AI incidents',         icon: Bot,      tone: 'ghost',     to: '/app/ai-incidents' },
      ]} />

      {/* unused-import safety net — keeps tree-shake honest */}
      <span className="hidden"><Network /></span>
    </div>
  )
}
